import React, { useEffect, useMemo, useState } from 'react';

import { SmartCardProvider } from '@atlaskit/link-provider';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { AnyAri } from '@atlassian/ari/any-ari';
import { getProductAndIDFromARI, type SearchPageResults } from '@atlassian/search-client';

import { ProductKeys3P } from '../../../common/constants/3p-product-configs';
import ErrorDark from '../../../common/constants/error/assets/error-dark.svg';
import ErrorLight from '../../../common/constants/error/assets/error-light.svg';
import {
	is3pProductKeyForAnalytics,
	ProductKeys,
	products1P,
} from '../../../common/constants/products';
import { ResultsStates } from '../../../common/constants/results';
import {
    type ConnectorSource,
	type ThirdPartyConfigsBootstrap,
	type ThirdPartyConfigsWithAPIResponse,
} from '../../../common/constants/schemas/3p-config';
import { ErrorComponent } from '../../../common/ui/error';
import { darkImageKey, lightImageKey, manageImageCaching } from '../../../common/ui/image-cache';
import { getLinksFromText } from '../../../common/ui/link-attachments';
import {
	LINKED_RESULTS_DEFAULT_EXPAND_SIZE,
	LINKED_RESULTS_TOP_K_INDEX_DEFAULT_EXPAND,
} from '../../../common/ui/linked-results';
import { ResultsLoading } from '../../../common/ui/results-loading';
import { ResultsNone } from '../../../common/ui/results-none';
import { searchResultsLoadUFOExperience } from '../../../common/ui/ufo/experiences';
import { onAdvancedSearchResultsShown } from '../../../common/utils/events/advanced-search-results-shown';
import { onFullPageSearchOperational } from '../../../common/utils/events/full-page-search-operational';
import type { ResultShown } from '../../../common/utils/events/types';
import { triggerExposureForWeakLinkExp } from '../../../common/utils/experiments';
import { ResultsFetchError } from '../../../common/utils/fetch-error';
import { getIsBulkConnectBannerShown } from '../../../common/utils/quick-find/analytics';
import {
	getIntegrationKey,
	getProductFromProviderId,
} from '../../../common/utils/search-result-utils';
import { getResultConnectorSource } from '../../../common/utils/third-party-utils';
import { useSafeIntercom } from '../../../common/utils/use-safe-intercom';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../../controllers/analytics';
import { useSearchAndResultAnalyticsAttributes } from '../../../controllers/analytics/search-and-response-analytics';
import { useSocialSignalsAnalyticsAttributes } from '../../../controllers/analytics/social-signal-analytics';
import { useSelectedFilters } from '../../../controllers/filters/selected';
import { useApplicationMode, useBootstrap, useBootstrapActions, useInitialTextQueryParam, useReferrer } from '../../../controllers/store/bootstrap';
import { useFetchFromCursor, useQueryState, useRequestedBackendExperiments, useResults, useResultsError, useResultsRequestId, useResultsState, useSearchActions, useSelectedProduct } from '../../../controllers/store/search';

import { Error } from './error';
import { Landing } from './landing';
import { Results } from './results';
import { getResultIdsAsString } from './utils';

const resultCountAndSortContainer = xcss({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	marginBottom: 'space.050',
});

const useFireAnalytics = (state: ResultsStates, thirdPartyConfigs: ThirdPartyConfigsBootstrap) => {
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [results] = useResults();
	const { fireAnalyticsEvent } = useAnalytics();
	const [queryState] = useQueryState();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();
	const socialSignalsAttributes = useSocialSignalsAnalyticsAttributes();
	const { searchAndResult } = useSearchAndResultAnalyticsAttributes();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [requestedBackendExperiments] = useRequestedBackendExperiments();
	const [referrer] = useReferrer();
	const initialTextQueryParam = useInitialTextQueryParam();
	const { clearReferrer } = useBootstrapActions();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const fetchFromCursor = useFetchFromCursor();
	const are3pResultsShown = are3pItemsInResults(results, thirdPartyConfigs);
	const resultIdsAsString = getResultIdsAsString(results);
	const resultsShown = getResultsShown(results, thirdPartyConfigs);

	useEffect(() => {
		if (state !== ResultsStates.NO_RESULTS && state !== ResultsStates.RESULTS) {
			return;
		}
		const numResults = results?.edges.length || 0;
		const isBulkConnectBannerShown = getIsBulkConnectBannerShown();

		fireAnalyticsEvent(
			onAdvancedSearchResultsShown({
				queryState,
				numResults,
				shownCount: numResults,
				are3pResultsShown,
				commonAttributes,
				nonPrivacySafeAttributes,
				socialSignalsAttributes,
				searchAndResult,
				resultsShown,
				requestedBackendExperiments,
				referrer: referrer && initialTextQueryParam ? referrer : undefined,
				isPaginatedQuery: !!fetchFromCursor,
				isBulkConnectBannerShown,
				productsWithTimeout: undefined,
			}),
		);

		fireAnalyticsEvent(
			onFullPageSearchOperational({
				success: true,
				commonAttributes,
			}),
		);

		// Ensure that the referrer attribute is only used once
		clearReferrer();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [resultIdsAsString]);
};

// export for testing purposes
export const are3pItemsInResults = (
	results: SearchPageResults | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
): boolean => {
	return !!results?.edges?.some(({ node }) => {
		const productKey =
			getProductFromProviderId(thirdPartyConfigs, node.providerId) ||
			getIntegrationKey(node.integrationId);
		if (!productKey) {
			// if no integration id it's a 1p
			return false;
		}
		return is3pProductKeyForAnalytics(productKey);
	});
};

export const ResultsContainer = (): React.JSX.Element => {
	const [{ thirdPartyConfigs }] = useBootstrap();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [results] = useResults();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const resultsState = useResultsState();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [requestId] = useResultsRequestId();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [error] = useResultsError();
	const [selectedProduct] = useSelectedProduct();
	const { retrySearch, resetSort, clearAllFilters } = useSearchActions();

	const { hasFiltersSelected } = useSelectedFilters({});
	const intercom = useSafeIntercom();
	const applicationMode = useApplicationMode();
	useFireAnalytics(resultsState, thirdPartyConfigs);

	if (
		thirdPartyConfigs?.[ProductKeys3P.GoogleDrive]?.impersonationEnabled &&
		selectedProduct === undefined
	) {
		triggerExposureForWeakLinkExp();
	}

	const [isOnline, _] = useState(navigator.onLine);

	useEffect(() => {
		if (results && results.edges && results.edges.length >= 0) {
			searchResultsLoadUFOExperience.mark('render_end');
			searchResultsLoadUFOExperience.success({
				metadata: { productFilter: selectedProduct, requestId, applicationMode },
			});

			// Only fire intercom results shown events when there are actually results
			// as we don't know when the user has connected their 3P provider or not
			if (results.edges.length > 0) {
				if (is3pProductKeyForAnalytics(selectedProduct)) {
					intercom?.trackEvent('search_results_shown_3p', { product_filter: selectedProduct });
				} else {
					intercom?.trackEvent('search_results_shown', { product_filter: selectedProduct });
				}
			}
		} else if (error && !results) {
			searchResultsLoadUFOExperience.failure({
				metadata: { productFilter: selectedProduct, applicationMode },
			});
		}
	}, [results, selectedProduct, error, intercom, requestId, applicationMode]);

	useEffect(() => {
		manageImageCaching(isOnline, ErrorLight, lightImageKey, ErrorDark, darkImageKey);
	}, [isOnline]);

	const showResultCountAndSort = useMemo(
		() => selectedProduct === ProductKeys.Confluence,
		[selectedProduct],
	);

	if (resultsState === ResultsStates.IS_LOADING) {
		return (
			<UFOLoadHold name="search-page-results-loading">
				<ResultsLoading />
			</UFOLoadHold>
		);
	}

	if (resultsState === ResultsStates.INITIAL) {
		return (
			<UFOLoadHold name="search-page-results-landing">
				<Landing />
			</UFOLoadHold>
		);
	}

	if (resultsState === ResultsStates.RESULTS) {
		return (
			<UFOSegment name="search-page-results">
				<Box>
					{showResultCountAndSort && (
						<Box xcss={resultCountAndSortContainer}>
							<Box>{/* TODO: Add result count component here */}</Box>
						</Box>
					)}
					<SmartCardProvider>
						<Results />
					</SmartCardProvider>
				</Box>
			</UFOSegment>
		);
	}

	if (resultsState === ResultsStates.NO_RESULTS) {
		return (
			<>
				<ResultsNone
					filtersEnabled={Boolean(selectedProduct) || hasFiltersSelected}
					clearFilters={() => {
						clearAllFilters();
						resetSort();
					}}
				/>
			</>
		);
	}

	return (
		<>
			{fg('result_error_info_in_non_prod') && (
				<>
					{process.env.NODE_ENV !== 'production' && (
						<ErrorComponent
							error={error || new ResultsFetchError('unknown result fetch error')}
							errorInfo={{}}
						/>
					)}
				</>
			)}
			<Error fetchResults={retrySearch} error={error} />
		</>
	);
};

export const getResultsShown = (
	results: SearchPageResults | undefined,
	thirdPartyConfigs: Partial<Record<ProductKeys3P, ThirdPartyConfigsWithAPIResponse>>,
): {
        id: any;
        product: string;
        position: number;
        metadata: {
            connectorType: ConnectorSource | undefined;
            boostedLinksShown: boolean | undefined;
            fanoutResultsShown: any;
            hasLinkInDescription: boolean;
        };
    }[] => {
	return (
		results?.edges?.map(({ node }, index) => {
			const productKey =
				getProductFromProviderId(thirdPartyConfigs, node.providerId) ||
				getIntegrationKey(node.integrationId);

			const [firstPartyProduct, , ,] = getProductAndIDFromARI(node.id);

			const resultAri = AnyAri.parse(node?.id || '');
			let hasLinkInDescription = false;
			if (resultAri.resourceOwner in products1P) {
				const links = getLinksFromText(node?.description ?? '');
				if (links && links.length > 0) {
					hasLinkInDescription = true;
				}
			}

			return {
				id: node.id,
				product: productKey || firstPartyProduct,
				position: index,
				metadata: {
					connectorType: getResultConnectorSource({
						connectorType: node.connectorType,
						resultNodeId: node.id,
						productKey: productKey,
						thirdPartyConfigs,
					}),
					boostedLinksShown: (node as any)?.linkedResults
						? (node as any)?.linkedResults.length > 0 &&
						index <= LINKED_RESULTS_TOP_K_INDEX_DEFAULT_EXPAND
						: undefined,
					fanoutResultsShown:
						node?.linkedResults &&
							node?.linkedResults.length > 0 &&
							index <= LINKED_RESULTS_TOP_K_INDEX_DEFAULT_EXPAND
							? node.linkedResults
								.slice(0, LINKED_RESULTS_DEFAULT_EXPAND_SIZE)
								.map((link: { id: any; category: any }) => {
									let is3P = false;
									try {
										is3P = !(AnyAri.parse(link.id).resourceOwner in products1P);
									} catch {
										is3P = false;
									}
									return {
										id: link.id,
										category: link.category,
										is3P,
									};
								})
							: undefined,
					hasLinkInDescription,
				},
			} satisfies ResultShown;
		}) || []
	);
};
